import React, { useEffect, useMemo, useState } from "react";
import { Plus, Trash2, CalendarDays, Users, Check, X, Clock3, UserCheck } from "lucide-react";
import { api, formatApiErrorDetail } from "@/lib/api";
import { toast } from "sonner";
import GermanDateInput from "@/components/GermanDateInput";
import TeamVacationMonthCalendar from "@/components/TeamVacationMonthCalendar";

const TYPES = [
  { k: "urlaub",      l: "Urlaub",         c: "#00CCC9" },
  { k: "krank",       l: "Krank",          c: "#EF4444" },
  { k: "fortbildung", l: "Fortbildung",    c: "#7C3AED" },
  { k: "dienstreise", l: "Dienstreise",    c: "#F59E0B" },
  { k: "sonstiges",   l: "Sonstiges",      c: "#94A3B8" },
];
const typeMeta = (k) => TYPES.find((t) => t.k === k) || TYPES[4];

const STATUS_META = {
  pending:  { label: "Beantragt",  cls: "bg-amber-50 text-amber-800 border-amber-200" },
  approved: { label: "Genehmigt",  cls: "bg-emerald-50 text-emerald-800 border-emerald-200" },
  rejected: { label: "Abgelehnt",  cls: "bg-red-50 text-red-800 border-red-200" },
};

const todayIso = () => new Date().toISOString().slice(0, 10);
const addDays = (iso, n) => {
  const d = new Date(iso + "T00:00:00");
  d.setDate(d.getDate() + n);
  return d.toISOString().slice(0, 10);
};
const weekdayLabel = (iso) => {
  const wd = ["So", "Mo", "Di", "Mi", "Do", "Fr", "Sa"][new Date(iso + "T00:00:00").getDay()];
  return `${wd} ${iso.slice(8, 10)}.`;
};
const fmtDe = (iso) => {
  if (!iso) return "";
  const [y, m, d] = iso.split("-");
  return `${d}.${m}.${y}`;
};

/**
 * Team vacation grid + own-vacation CRUD.
 * - Any user can add/delete their own vacations.
 * - Admin/Manager can add for others (member picker) and delete anyone.
 */
export default function TeamVacationsWidget({ currentUser }) {
  const isAdmin = currentUser?.role === "admin" || currentUser?.role === "manager";
  const [rangeStart, setRangeStart] = useState(todayIso());
  const [rangeEnd, setRangeEnd] = useState(addDays(todayIso(), 20));
  const [grid, setGrid] = useState([]);
  const [rows, setRows] = useState([]);
  const [pending, setPending] = useState({ can_approve: false, rows: [] });
  const [loading, setLoading] = useState(true);
  const [creating, setCreating] = useState(false);
  const [deciding, setDeciding] = useState(null);
  const [approvingAll, setApprovingAll] = useState(false);
  const [form, setForm] = useState({
    start_date: todayIso(),
    end_date: todayIso(),
    type: "urlaub",
    note: "",
    user_id: "",
  });
  const [team, setTeam] = useState([]);
  const [balance, setBalance] = useState(null);
  const [delegableTasks, setDelegableTasks] = useState([]);
  const [selectedSubs, setSelectedSubs] = useState([]);
  const [taskDelegation, setTaskDelegation] = useState({});

  const load = async () => {
    setLoading(true);
    try {
      const [t, p, b] = await Promise.all([
        api.get("/vacations/team", { params: { start: rangeStart, end: rangeEnd } }),
        api.get("/vacations/pending"),
        api.get("/vacations/balance"),
      ]);
      setGrid(t.data.grid || []);
      setRows(t.data.rows || []);
      setPending(p.data || { can_approve: false, rows: [] });
      setBalance(b.data);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    } finally { setLoading(false); }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [rangeStart, rangeEnd]);

  // Team list (for admin: for-user + substitute picker; for everyone: substitute picker).
  // Uses /boards/team-members (readable by all staff) instead of /coaches (admin-only full list),
  // so non-admins can also see colleagues to pick as a substitute.
  useEffect(() => {
    api.get("/boards/team-members").then((r) => setTeam(r.data || [])).catch(() => {});
  }, []);

  // Load the target person's open tasks/goals so they can be delegated to specific substitutes.
  useEffect(() => {
    if (form.type !== "urlaub") { setDelegableTasks([]); return; }
    const targetUid = isAdmin ? (form.user_id || currentUser?.user_id) : currentUser?.user_id;
    if (!targetUid) return;
    const params = targetUid !== currentUser?.user_id ? { user_id: targetUid } : {};
    api.get("/kpi/subtasks", { params })
      .then((r) => setDelegableTasks((r.data || []).filter((t) => t.status !== "done")))
      .catch(() => setDelegableTasks([]));
  }, [form.type, form.user_id, isAdmin, currentUser?.user_id]);

  const toggleSub = (uid) => {
    setSelectedSubs((prev) => (prev.includes(uid) ? prev.filter((x) => x !== uid) : [...prev, uid]));
  };
  const toggleTaskForSub = (uid, taskId) => {
    setTaskDelegation((prev) => {
      const cur = prev[uid] || [];
      const next = cur.includes(taskId) ? cur.filter((x) => x !== taskId) : [...cur, taskId];
      return { ...prev, [uid]: next };
    });
  };

  // 14-Tage-Mindestvorlauf für nicht-privilegierte Nutzer (ersetzt die alte 30-Tage-Obergrenze)
  const isTooSoon = useMemo(() => {
    if (isAdmin || form.type !== "urlaub" || !form.start_date) return false;
    const today = new Date(); today.setHours(0,0,0,0);
    const start = new Date(form.start_date + "T00:00:00");
    return (start - today) / 86400000 < 14;
  }, [form.start_date, form.type, isAdmin]);

  const days = useMemo(() => {
    const out = [];
    let c = rangeStart;
    // Hard cap the grid to 60 days to prevent runaway DOM (a wrong end-date could otherwise render thousands of columns)
    let safety = 60;
    while (c <= rangeEnd && safety-- > 0) { out.push(c); c = addDays(c, 1); }
    return out;
  }, [rangeStart, rangeEnd]);

  const submit = async () => {
    if (!form.start_date || !form.end_date) { toast.error("Bitte Zeitraum wählen"); return; }
    if (form.end_date < form.start_date) { toast.error("Ende darf nicht vor Start liegen"); return; }
    if (form.type === "urlaub" && selectedSubs.length === 0) { toast.error("Bitte mindestens eine Vertretung wählen"); return; }
    setCreating(true);
    try {
      const payload = {
        start_date: form.start_date,
        end_date: form.end_date,
        type: form.type,
        reason: form.note,
      };
      if (isAdmin && form.user_id) payload.coach_user_id = form.user_id;
      if (selectedSubs.length > 0) {
        payload.substitutes = selectedSubs.map((uid) => ({ user_id: uid, task_ids: taskDelegation[uid] || [] }));
      }
      await api.post("/vacations", payload);
      toast.success(form.type === "krank" ? "Krankmeldung eingetragen" : "Antrag gestellt · warte auf Genehmigung");
      setForm({ start_date: todayIso(), end_date: todayIso(), type: "urlaub", note: "", user_id: "" });
      setSelectedSubs([]);
      setTaskDelegation({});
      load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    } finally { setCreating(false); }
  };

  const decide = async (id, decision, opts = {}) => {
    let note = opts.note;
    if (decision === "reject" && !note) {
      note = window.prompt("Grund der Ablehnung (optional):", "") || null;
    }
    setDeciding(id);
    try {
      await api.post(`/vacations/${id}/decision`, { decision, note });
      toast.success(decision === "approve" ? "Antrag genehmigt" : "Antrag abgelehnt");
      load();
      window.dispatchEvent(new Event("vacations:changed"));
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    } finally { setDeciding(null); }
  };

  const approveAll = async () => {
    if (!window.confirm(`${pending.rows.length} offene Anträge alle auf einmal genehmigen?`)) return;
    setApprovingAll(true);
    try {
      const { data } = await api.post("/vacations/approve-all");
      toast.success(`${data.approved_count} Anträge genehmigt`);
      load();
      window.dispatchEvent(new Event("vacations:changed"));
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    } finally { setApprovingAll(false); }
  };

  const remove = async (id) => {
    if (!window.confirm("Diesen Eintrag wirklich löschen?")) return;
    try {
      await api.delete(`/vacations/${id}`);
      toast.success("Gelöscht");
      load();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message); }
  };

  const nUsers = grid.length;
  const nDays = rows.reduce((s, r) => {
    const a = new Date(r.start_date + "T00:00:00");
    const b = new Date(r.end_date + "T00:00:00");
    return s + Math.max(1, Math.round((b - a) / 86400000) + 1);
  }, 0);

  return (
    <div className="border border-slate-200 bg-white" data-testid="team-vacations-widget">
      <div className="p-5 border-b border-slate-200 flex items-baseline justify-between flex-wrap gap-3">
        <div>
          <div className="text-[10px] font-bold uppercase tracking-overline text-slate-500 flex items-center gap-2">
            <Users size={11} strokeWidth={1.5} /> Team
          </div>
          <div className="font-heading text-xl font-light tracking-tighter text-slate-900">Urlaubs- & Abwesenheits-Übersicht</div>
        </div>
        <div className="flex items-center gap-4 text-xs">
          {balance && (
            <div className="flex items-center gap-3 border border-slate-200 px-4 py-2 bg-slate-50" data-testid="vacation-balance">
              <div>
                <div className="text-[10px] font-bold uppercase tracking-overline text-slate-500">Mein Rest-Urlaub {balance.year}</div>
                <div className="font-mono text-slate-900 text-lg">
                  <span className={balance.remaining_days <= 5 ? "text-red-700" : ""}>{balance.remaining_days}</span>
                  <span className="text-slate-400 text-sm"> / {balance.quota} Tage</span>
                </div>
              </div>
              <div className="text-[10px] text-slate-500 font-mono leading-tight">
                <div>{balance.used_days} genommen</div>
                {balance.pending_days > 0 && <div className="text-amber-700">{balance.pending_days} in Prüfung</div>}
              </div>
            </div>
          )}
          <span className="font-mono text-slate-500">{nUsers} {nUsers === 1 ? "Person" : "Personen"} · {nDays} Tage</span>
        </div>
      </div>

      {/* Großer Team-Urlaubskalender (Monatsansicht) */}
      <div className="p-5 border-b border-slate-200 bg-slate-50/50">
        <TeamVacationMonthCalendar isAdmin={currentUser?.role === "admin"} />
      </div>

      {/* Neuer Eintrag */}
      <div className="p-5 border-b border-slate-200 bg-slate-50" data-testid="vacation-new-form">
        <div className="text-[10px] font-bold uppercase tracking-overline text-slate-500 mb-2">Neuer Eintrag</div>
        <div className="grid grid-cols-1 md:grid-cols-[1fr_1fr_1fr_1fr_auto] gap-3 items-end">
          {isAdmin ? (
            <div>
              <label className="text-[10px] font-bold uppercase tracking-overline text-slate-500 mb-1 block">Für Mitarbeiter (leer = ich)</label>
              <select className="input-tech" value={form.user_id} onChange={(e) => setForm({ ...form, user_id: e.target.value })} data-testid="vac-user-select">
                <option value="">Ich ({currentUser?.name})</option>
                {team.map((t) => (
                  <option key={t.user_id} value={t.user_id}>{t.name}</option>
                ))}
              </select>
            </div>
          ) : (
            <div>
              <label className="text-[10px] font-bold uppercase tracking-overline text-slate-500 mb-1 block">Für</label>
              <div className="input-tech text-sm bg-white text-slate-500">{currentUser?.name}</div>
            </div>
          )}
          <div>
            <label className="text-[10px] font-bold uppercase tracking-overline text-slate-500 mb-1 block">Von</label>
            <GermanDateInput className="input-tech font-mono" value={form.start_date} onChange={(e) => setForm({ ...form, start_date: e.target.value })} data-testid="vac-start" />
          </div>
          <div>
            <label className="text-[10px] font-bold uppercase tracking-overline text-slate-500 mb-1 block">Bis</label>
            <GermanDateInput className="input-tech font-mono" value={form.end_date} onChange={(e) => setForm({ ...form, end_date: e.target.value })} data-testid="vac-end" />
          </div>
          <div>
            <label className="text-[10px] font-bold uppercase tracking-overline text-slate-500 mb-1 block">Art</label>
            <select className="input-tech" value={form.type} onChange={(e) => setForm({ ...form, type: e.target.value })} data-testid="vac-type">
              {TYPES.map((t) => <option key={t.k} value={t.k}>{t.l}</option>)}
            </select>
          </div>
          <button onClick={submit} disabled={creating} data-testid="vac-add-btn"
            className="px-4 py-2.5 bg-slate-900 text-white text-xs font-medium hover:bg-black disabled:opacity-40 flex items-center gap-1 h-fit">
            <Plus size={12} strokeWidth={2} /> {form.type === "krank" ? "Krankmeldung" : "Beantragen"}
          </button>
        </div>
        <input value={form.note} onChange={(e) => setForm({ ...form, note: e.target.value })}
          className="input-tech text-sm mt-3" placeholder="Notiz (optional)" data-testid="vac-note" />

        {/* Substitute picker (Vertretung) — Pflicht bei Urlaub, mehrere möglich + Aufgaben-Delegation */}
        <div className="mt-3">
          <label className="text-[10px] font-bold uppercase tracking-overline text-slate-500 mb-1 flex items-center gap-1">
            <UserCheck size={11} strokeWidth={1.5} /> Vertretung(en) {form.type === "urlaub" ? <span className="text-red-600">*</span> : "(optional)"}
          </label>
          <div className="border border-slate-200 bg-white divide-y divide-slate-100 max-h-72 overflow-y-auto" data-testid="vac-substitutes-picker">
            {team.filter((t) => t.user_id !== (form.user_id || currentUser?.user_id)).map((t) => {
              const checked = selectedSubs.includes(t.user_id);
              return (
                <div key={t.user_id} className="p-2.5">
                  <label className="flex items-center gap-2 text-xs cursor-pointer">
                    <input type="checkbox" checked={checked} onChange={() => toggleSub(t.user_id)} data-testid={`vac-substitute-checkbox-${t.user_id}`} />
                    <span className="font-medium text-slate-800">{t.name}</span>
                  </label>
                  {checked && delegableTasks.length > 0 && (
                    <div className="mt-2 ml-6 space-y-1" data-testid={`vac-substitute-tasks-${t.user_id}`}>
                      <div className="text-[9px] font-bold uppercase tracking-overline text-slate-400 mb-1">Aufgaben/Ziele zuweisen (optional)</div>
                      {delegableTasks.map((task) => (
                        <label key={task.task_id} className="flex items-center gap-2 text-[11px] text-slate-600 cursor-pointer">
                          <input type="checkbox" checked={(taskDelegation[t.user_id] || []).includes(task.task_id)}
                            onChange={() => toggleTaskForSub(t.user_id, task.task_id)}
                            data-testid={`vac-task-assign-${t.user_id}-${task.task_id}`} />
                          {task.title}
                        </label>
                      ))}
                    </div>
                  )}
                  {checked && delegableTasks.length === 0 && (
                    <div className="mt-1 ml-6 text-[11px] text-slate-400 italic">Keine offenen Aufgaben zum Delegieren.</div>
                  )}
                </div>
              );
            })}
            {team.length === 0 && <div className="p-3 text-xs text-slate-400 italic">Keine Team-Mitglieder gefunden.</div>}
          </div>
          <p className="text-[10px] text-slate-500 mt-1">
            {form.type === "urlaub"
              ? "Pflichtfeld · Wähle eine oder mehrere Personen als Vertretung. Optional weist du jeder Person konkrete Aufgaben/Ziele zu — sie sehen diese unter „Meine Aufgaben“, solange dein Urlaub läuft."
              : "Optional · Bei Krankmeldung/Fortbildung keine Vertretung nötig."}
          </p>
        </div>


        {/* 14-Tage-Mindestvorlauf-Warnung für nicht-Admin */}
        {isTooSoon && (
          <div className="mt-3 border border-amber-300 bg-amber-50 text-amber-900 px-4 py-3 text-xs" data-testid="vac-min-notice-warning">
            <b>Achtung: Weniger als 14 Tage bis Urlaubsbeginn.</b> Urlaubsanträge müssen mindestens 14 Kalendertage
            vor Beginn gestellt werden. Für kurzfristige Fälle bitte die Geschäftsführung direkt ansprechen.
          </div>
        )}
      </div>

      {/* Pending-Queue (nur wenn es offene Anträge gibt) */}
      {pending.rows && pending.rows.length > 0 && (
        <div className="border-b border-amber-200 bg-amber-50/60 p-5" data-testid="vacation-pending-queue">
          <div className="text-[10px] font-bold uppercase tracking-overline text-amber-800 mb-3 flex items-center justify-between gap-2">
            <span className="flex items-center gap-2">
              <Clock3 size={11} strokeWidth={1.5} />
              {pending.can_approve ? "Offene Anträge · warten auf deine Entscheidung" : "Meine offenen Anträge"}
            </span>
            {pending.can_approve && pending.rows.length > 1 && (
              <button onClick={approveAll} disabled={approvingAll} data-testid="vac-approve-all-btn"
                className="px-3 py-1.5 bg-emerald-600 text-white text-[10px] font-bold uppercase tracking-overline hover:bg-emerald-700 disabled:opacity-40 flex items-center gap-1">
                <Check size={11} strokeWidth={2} /> Alle {pending.rows.length} genehmigen
              </button>
            )}
          </div>
          <ul className="space-y-2">
            {pending.rows.map((r) => {
              const meta = typeMeta(r.type);
              return (
                <li key={r.vacation_id} className="flex flex-wrap items-center gap-3 border border-amber-200 bg-white px-3 py-2" data-testid={`pending-${r.vacation_id}`}>
                  <span className="w-2 h-2 rounded-full flex-shrink-0" style={{ background: meta.c }} />
                  <div className="flex-1 min-w-[220px] text-sm">
                    <span className="font-medium text-slate-900">{r.coach_name || r.user_name}</span>
                    <span className="text-slate-500"> · {meta.l} · {fmtDe(r.start_date)}{r.start_date !== r.end_date ? ` → ${fmtDe(r.end_date)}` : ""}</span>
                    {r.reason && <span className="text-slate-400 text-xs"> · {r.reason}</span>}
                    {r.substitutes && r.substitutes.length > 0 && (
                      <span className="text-slate-500 text-xs" data-testid={`pending-substitutes-${r.vacation_id}`}>
                        {" "}· Vertretung: {r.substitutes.map((s) => `${s.name}${(s.tasks || []).length ? ` (${s.tasks.length} Aufgabe${s.tasks.length > 1 ? "n" : ""})` : ""}`).join(", ")}
                      </span>
                    )}
                  </div>
                  {pending.can_approve ? (
                    <div className="flex items-center gap-2">
                      <button onClick={() => decide(r.vacation_id, "approve")}
                        disabled={deciding === r.vacation_id}
                        data-testid={`approve-${r.vacation_id}`}
                        className="px-3 py-1.5 bg-emerald-600 text-white text-xs font-medium hover:bg-emerald-700 disabled:opacity-40 flex items-center gap-1">
                        <Check size={11} strokeWidth={2} /> Genehmigen
                      </button>
                      <button onClick={() => decide(r.vacation_id, "reject")}
                        disabled={deciding === r.vacation_id}
                        data-testid={`reject-${r.vacation_id}`}
                        className="px-3 py-1.5 border border-red-300 text-red-700 text-xs font-medium hover:bg-red-50 disabled:opacity-40 flex items-center gap-1">
                        <X size={11} strokeWidth={2} /> Ablehnen
                      </button>
                    </div>
                  ) : (
                    <span className="text-[10px] font-bold uppercase tracking-overline text-amber-700">wartet auf Genehmigung</span>
                  )}
                </li>
              );
            })}
          </ul>
        </div>
      )}

      {/* Range picker */}
      <div className="p-4 border-b border-slate-200 flex items-center gap-3 flex-wrap">
        <div className="text-[10px] font-bold uppercase tracking-overline text-slate-500 flex items-center gap-2"><CalendarDays size={11} strokeWidth={1.5} /> Ansicht</div>
        <GermanDateInput className="input-tech font-mono w-40" value={rangeStart} onChange={(e) => setRangeStart(e.target.value)} data-testid="vac-range-from" />
        <span className="text-slate-400 text-xs">bis</span>
        <GermanDateInput className="input-tech font-mono w-40" value={rangeEnd} onChange={(e) => setRangeEnd(e.target.value)} data-testid="vac-range-to" />
        <div className="flex-1" />
        <div className="flex items-center gap-3 text-[11px] flex-wrap">
          {TYPES.map((t) => (
            <span key={t.k} className="flex items-center gap-1">
              <span className="w-3 h-3" style={{ background: t.c }} /> {t.l}
            </span>
          ))}
        </div>
      </div>

      {/* Grid */}
      <div className="overflow-x-auto" data-testid="vacation-grid">
        {loading ? (
          <div className="p-6 text-sm text-slate-500">Lädt…</div>
        ) : grid.length === 0 ? (
          <div className="p-8 text-sm text-slate-400 italic text-center">Keine Abwesenheiten in diesem Zeitraum.</div>
        ) : (
          <table className="min-w-full text-xs">
            <thead>
              <tr className="border-b border-slate-200 bg-slate-50">
                <th className="text-left p-2 sticky left-0 bg-slate-50 z-10 font-medium">Mitarbeiter</th>
                {days.map((d) => (
                  <th key={d} className="p-1 text-center font-mono text-[10px] text-slate-500 font-medium min-w-[36px]">
                    {weekdayLabel(d)}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {grid.map((row) => (
                <tr key={row.user_id} className="border-b border-slate-100" data-testid={`vac-row-${row.user_id}`}>
                  <td className="p-2 sticky left-0 bg-white font-medium">{row.user_name || row.user_id}</td>
                  {days.map((d) => {
                    const t = row.days[d];
                    const meta = t ? typeMeta(t) : null;
                    return (
                      <td key={d} className="text-center p-0" title={meta?.l || ""}>
                        {meta && <div className="w-full h-6" style={{ background: meta.c }} />}
                      </td>
                    );
                  })}
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {/* Alle Einträge in Liste (löschen) */}
      {rows.length > 0 && (
        <div className="border-t border-slate-200 p-5">
          <div className="text-[10px] font-bold uppercase tracking-overline text-slate-500 mb-2">Einträge im Zeitraum</div>
          <ul className="space-y-1">
            {rows.map((r) => {
              const meta = typeMeta(r.type);
              const sMeta = STATUS_META[r.status] || STATUS_META.approved;
              const canDelete = isAdmin || r.user_id === currentUser?.user_id;
              return (
                <li key={r.vacation_id} className="flex items-center gap-2 border border-slate-100 px-3 py-2" data-testid={`vac-item-${r.vacation_id}`}>
                  <span className="w-2 h-2 rounded-full flex-shrink-0" style={{ background: meta.c }} />
                  <div className="flex-1 min-w-0 text-sm">
                    <span className="font-medium text-slate-900">{r.user_name}</span>
                    <span className="text-slate-500"> · {meta.l} · {fmtDe(r.start_date)}{r.start_date !== r.end_date ? ` → ${fmtDe(r.end_date)}` : ""}</span>
                    {r.note && <span className="text-slate-400 text-xs"> · {r.note}</span>}
                    {(r.substitutes && r.substitutes.length > 0) ? (
                      <span className="text-slate-500 text-xs" data-testid={`vac-item-substitutes-${r.vacation_id}`}>
                        {" "}· Vertretung: {r.substitutes.map((s) => `${s.name}${(s.tasks || []).length ? ` (${s.tasks.length} Aufgabe${s.tasks.length > 1 ? "n" : ""})` : ""}`).join(", ")}
                      </span>
                    ) : r.substitute_name && (
                      <span className="text-slate-500 text-xs"> · Vertretung: <b>{r.substitute_name}</b></span>
                    )}
                    {r.status === "rejected" && r.decision_note && (
                      <span className="text-red-600 text-xs"> · Grund: {r.decision_note}</span>
                    )}
                    {r.approver_name && r.status !== "pending" && (
                      <span className="text-slate-400 text-[10px]"> · von {r.approver_name}</span>
                    )}
                  </div>
                  <span className={`text-[10px] font-bold uppercase tracking-overline px-2 py-0.5 border ${sMeta.cls}`}>{sMeta.label}</span>
                  {canDelete && (
                    <button onClick={() => remove(r.vacation_id)} className="text-slate-400 hover:text-red-600" title="Löschen">
                      <Trash2 size={12} strokeWidth={1.5} />
                    </button>
                  )}
                </li>
              );
            })}
          </ul>
        </div>
      )}
    </div>
  );
}
